import type { Component } from "schemas/component";
import type { SchemaReploShopifyProduct } from "schemas/generated/product";

import React, { useState } from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { selectActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import Chip from "@v1/components/designSystem/chip/SelectableChip";
import { useV1AIStreaming } from "@v1/providers/AIStreamingProvider";
import {
  selectComponentData,
  selectDraftComponents,
  selectRootComponent,
  selectStreamingUpdate,
  setEditorMode,
  setStreamingUpdate,
} from "@v1/reducers/core-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import {
  getComponentAlignment,
  getComponentPositioning,
  getIconAndMessage,
} from "@v1/utils/tree-utils";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { BadgeCheck } from "lucide-react";
import { animated, config, useTransition } from "react-spring";
import { editorCanvasToMediaSize } from "replo-runtime/shared/utils/breakpoints";

import BuildAssistantGenerationLoader from "./BuildAssistantGenerationLoader";
import { ExpandedBuildAssistantPrompt } from "./ExpandedBuildAssistantPrompt";
import { MinimizedBuildAssistantPrompt } from "./MinimizedBuildAssistantPrompt";

// Maximum number of components that can be selected for build assistant
// to prevent server overload from screenshot generation
const MAX_COMPONENTS_FOR_BUILD_ASSISTANT = 10;

interface BuildAssistantProps {
  isMinimized: boolean;
}

export const BuildAssistant: React.FC<BuildAssistantProps> = ({
  isMinimized,
}) => {
  const [prompt, setPrompt] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<SchemaReploShopifyProduct | null>();
  const [showGenerationDone, setShowGenerationDone] = useState(false);
  const [fadeTransition, setFadeTransition] = useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const dispatch = useEditorDispatch();
  const store = useEditorStore();
  const logEvent = useLogAnalytics();
  const { abort, initiateParallelGeneration, applyChanges, isGenerating } =
    useV1AIStreaming();

  const draftComponents = useEditorSelector(selectDraftComponents);
  const rootComponent = selectRootComponent(store.getState());

  // Determine target components with limit applied
  let allTargetComponents: Component[] = [];
  if (draftComponents.length > 0) {
    allTargetComponents = draftComponents;
  } else if (rootComponent) {
    allTargetComponents = [rootComponent];
  }

  // Apply component limit for build assistant to prevent server overload
  const targetComponents = allTargetComponents.slice(
    0,
    MAX_COMPONENTS_FOR_BUILD_ASSISTANT,
  );
  const hasExceededLimit =
    allTargetComponents.length > MAX_COMPONENTS_FOR_BUILD_ASSISTANT;

  const handleBuild = () => {
    if (!prompt) {
      return;
    }

    logEvent("ai.action.submitted", {
      prompt,
      triggeringFeature: "build-assistant",
    });

    if (!rootComponent) {
      return;
    }

    const componentsToGenerate = targetComponents;

    initiateParallelGeneration(
      {
        type: "template",
        userPrompt: prompt,
        nonce: 0,
        components: componentsToGenerate,
        selectedProduct,
        triggeringFeature: "build-assistant",
      },
      () => {
        onBuildComplete();
      },
    );
  };

  const handleAbort = () => {
    logEvent("ai.action.aborted", {
      triggeringFeature: "build-assistant",
    });
    abort();
  };

  const onBuildComplete = () => {
    setPrompt("");
    dispatch(setEditorMode("edit"));

    const streamingUpdate = selectStreamingUpdate(store.getState());

    if (streamingUpdate?.id) {
      logEvent("ai.action.approved", {
        streamingUpdateId: streamingUpdate?.id,
        model: "claude-sonnet-4-5-20250929",
        triggeringFeature: "build-assistant",
      });
    }

    dispatch(setStreamingUpdate(streamingUpdate));
    applyChanges();
    dispatch(setStreamingUpdate(null));

    setShowGenerationDone(true);
    setFadeTransition(true);

    setTimeout(() => {
      setFadeTransition(false);
      setTimeout(() => {
        setShowGenerationDone(false);
        setTimeout(() => {
          if (isMinimized) {
            inputRef.current?.focus();
          } else {
            textareaRef.current?.focus();
          }
        }, 100);
      }, 500);
    }, 1000);
  };

  const doneTransition = useTransition(fadeTransition, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.gentle,
  });

  const buildAssistantGenerating = (
    <BuildAssistantGenerationLoader handleAbort={handleAbort} />
  );

  const doneStateContent = doneTransition((styles, item) => {
    return (
      item && (
        <animated.div
          style={styles}
          className="flex w-full items-center gap-2 p-3"
        >
          <DeprecatedIconButton
            variant="secondary"
            size="sm"
            icon={<BadgeCheck className="text-emerald" size={12} />}
            UNSAFE_className="bg-emerald-soft border-0.5 border-emerald/10 hover:bg-emerald-soft cursor-default"
          />
          <span className="text-xs font-medium">Done!</span>
        </animated.div>
      )
    );
  });

  const minimizedBuildAssistant = (
    <MinimizedBuildAssistantPrompt
      prompt={prompt}
      setPrompt={setPrompt}
      placeholderText="Ask for any edits or customizations..."
      onSubmit={handleBuild}
      isGenerating={isGenerating}
      inputRef={inputRef}
    />
  );

  const expandedBuildAssistant = (
    <>
      {hasExceededLimit && (
        <div className="p-3 pb-0">
          <InlineAlert variant="warning" multiline>
            <div>
              <p className="font-medium">Component Limit Reached</p>
              <p className="text-xs mt-1">
                You can select up to {MAX_COMPONENTS_FOR_BUILD_ASSISTANT}{" "}
                components at a time. We'll use the first{" "}
                {MAX_COMPONENTS_FOR_BUILD_ASSISTANT} of your{" "}
                {allTargetComponents.length} selected components.
              </p>
            </div>
          </InlineAlert>
        </div>
      )}
      <div className="flex items-start justify-between gap-1 p-3">
        <div className="flex flex-wrap items-center gap-1">
          {targetComponents.map((component) => {
            const activeCanvas = selectActiveCanvas(store.getState());
            const componentPosition = getComponentPositioning(
              editorCanvasToMediaSize[activeCanvas].mediaSize,
              component ?? undefined,
            );
            const componentHasActions = Boolean(
              component?.props?.onClick?.length ||
                component?.props?.onHover?.length,
            );
            const componentAlignment = getComponentAlignment(
              editorCanvasToMediaSize[activeCanvas].mediaSize,
              component ?? undefined,
            );
            const isPageRoot = component.id === rootComponent?.id;

            const { icon } = getIconAndMessage(
              isPageRoot,
              component.props.direction,
              component.type,
              component.props.iconName,
              componentPosition,
              componentHasActions,
              component.name ?? "",
              componentAlignment,
              null,
            );

            const componentData = selectComponentData(
              store.getState(),
              component.id,
            );
            const label = isPageRoot
              ? "Page"
              : componentData?.label || component.name || component.type;

            return (
              <Chip
                key={component.id}
                size="sm"
                active={false}
                UNSAFE_className="hover:bg-transparent cursor-default"
                layoutClassName="max-w-80"
                startEnhancer={icon}
              >
                {label}
              </Chip>
            );
          })}
        </div>
      </div>

      <Separator orientation="horizontal" />

      <ExpandedBuildAssistantPrompt
        prompt={prompt}
        setPrompt={setPrompt}
        placeholderText="Ask for any edits or customizations..."
        selectedProduct={selectedProduct ?? null}
        setSelectedProduct={setSelectedProduct}
        onSubmit={handleBuild}
        showBrandContextPreview={false}
        showFontSample={false}
        colorOverlapSize="large"
        isGenerating={isGenerating}
        inputRef={textareaRef}
      />
    </>
  );

  let buildAssistantContent;
  if (isGenerating) {
    buildAssistantContent = buildAssistantGenerating;
  } else if (showGenerationDone) {
    buildAssistantContent = doneStateContent;
  } else if (isMinimized) {
    buildAssistantContent = minimizedBuildAssistant;
  } else {
    buildAssistantContent = expandedBuildAssistant;
  }

  return buildAssistantContent;
};
